/**
 * フォーム定義ファイルの名前の規約。
 *
 * SpiffArena の User Task は `formJsonSchemaFilename` / `formUiSchemaFilename` の
 * 2 つでフォームを指す。前者は項目を定義する JSON Schema、後者は並び順や
 * ウィジェットを指定する UI Schema で、役割が違う。名前で見分けられるようにして
 * おかないと、紐付けの候補に uischema が混ざり「項目が 1 つも出ないフォーム」を
 * 選べてしまう（標準 UI と同じ規約に揃える）。
 */

/** 並び順などを指定する UI Schema。 */
const UI_SCHEMA_NAME = /^[a-z0-9][a-z0-9-]*(?:-|\.)uischema\.json$/;

/** 項目を定義する JSON Schema。 */
const SCHEMA_NAME = /^[a-z0-9][a-z0-9-]*(?:-|\.)schema\.json$/;

export function isFormUiSchemaFile(name: string): boolean {
  return UI_SCHEMA_NAME.test(name);
}

/** `*-uischema.json` は除く（`-schema.json` にも一致してしまうため）。 */
export function isFormSchemaFile(name: string): boolean {
  return SCHEMA_NAME.test(name) && !isFormUiSchemaFile(name);
}

export type SplitFormFiles = {
  readonly schemas: readonly string[];
  readonly uiSchemas: readonly string[];
};

/** 紐付けの候補を役割ごとに分ける。規約から外れた名前はどちらにも入れない。 */
export function splitFormFiles(names: readonly string[]): SplitFormFiles {
  return {
    schemas: names.filter(isFormSchemaFile),
    uiSchemas: names.filter(isFormUiSchemaFile),
  };
}

export type NewFormFileNameIssue =
  | "empty"
  /** パス区切り・大文字・空白など、ファイル名として使えない文字がある。 */
  | "invalid"
  /** `*-schema.json` / `*-uischema.json` の規約に合わない。 */
  | "convention"
  | "duplicate";

export type NewFormFileNameCheck =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: NewFormFileNameIssue };

/** ファイル名として安全な形（パスを含まない・英小文字と数字とハイフン）。 */
const SAFE_NAME = /^[a-z0-9][a-z0-9-]*\.[a-z0-9.]+$/;

/**
 * 新しいフォーム定義のファイル名を検証する。
 *
 * 規約に合わない名前を許すと、作れはするが紐付けの候補に出てこないファイルが
 * できる（利用者からは「作ったのに選べない」に見える）。
 */
export function validateNewFormFileName(
  name: string,
  existing: readonly string[],
): NewFormFileNameCheck {
  const trimmed = name.trim();
  if (trimmed === "") return { ok: false, reason: "empty" };
  if (!SAFE_NAME.test(trimmed)) return { ok: false, reason: "invalid" };

  if (!isFormSchemaFile(trimmed) && !isFormUiSchemaFile(trimmed)) {
    return { ok: false, reason: "convention" };
  }

  if (existing.includes(trimmed)) return { ok: false, reason: "duplicate" };

  return { ok: true };
}
